/**
 * The planning list's full-plan export: one row per route per day, with costs and load factors.
 *
 * It carries everything the table shows the login asking for it, and the columns the table
 * withholds from a login are withheld here too (COLUMN_NEEDS). The figures it is handed are the
 * same ones the table renders, so the file and the screen cannot disagree; this module only lays
 * them out as cells.
 */
import { format } from "date-fns"

import {
  COVER_LABEL,
  describeCover,
  displayPerson,
  formatWindow,
  worstException,
} from "@/lib/shiftCover"
import {
  applyPlannedMileageMultiplier,
  calculateDistanceFromPlannedStops,
  calculateDurationMinutes,
  findVehicleType,
  type EmptyLegsDetail,
  type VehicleCapacityInfo,
} from "@/lib/routeCostCalculation"
import type { PlanningRouteCost } from "@/lib/planningRoutes"
import type { CoveredRoute, PlannedRoute, ShiftException, VehicleType } from "@/types/collections"

export const PLANNING_LIST_HEADERS = [
  "Date",
  "Day",
  "Route Code",
  "Shippers",
  "Vehicle Planned",
  "Vehicle Range (miles)",
  "Total Mileage (P2P)",
  "Planned Start",
  "Planned End",
  "Duration (min)",
  "Total Stops",
  "Volume",
  "Load Factor (%)",
  "Shift Cover",
  "Empty Legs %",
  "Empty Legs (count)",
  "Est. Cost (Total)",
  "Vehicle Lease Cost",
  "Charging Cost",
  "Driver Wage Cost",
  "Haulage Delivery Revenue",
  "Net Cost",
  "Cost per Parcel",
  "Charging Internal Miles",
  "Charging External Miles",
]

/**
 * The columns that are only written for a login with the capability named.
 *
 * The same split the table makes: money, load factor and the vehicle data they are worked out
 * from need `costs`, and booked shifts need `shiftCover`. A column not listed goes to everyone.
 */
const COLUMN_NEEDS: Record<string, "costs" | "shiftCover"> = {
  "Vehicle Range (miles)": "costs",
  "Load Factor (%)": "costs",
  "Shift Cover": "shiftCover",
  "Empty Legs %": "costs",
  "Empty Legs (count)": "costs",
  "Est. Cost (Total)": "costs",
  "Vehicle Lease Cost": "costs",
  "Charging Cost": "costs",
  "Driver Wage Cost": "costs",
  "Haulage Delivery Revenue": "costs",
  "Net Cost": "costs",
  "Cost per Parcel": "costs",
  "Charging Internal Miles": "costs",
  "Charging External Miles": "costs",
}

/** One route's row, with the figures the table already worked out for it. */
export interface PlanningListExportRoute {
  route: PlannedRoute
  /** Forecast parcels across the route's stops. */
  volume: number
  /** Null where the route has no vehicle type or no stop that takes up capacity. */
  capacityInfo: VehicleCapacityInfo | null
  emptyLegs: EmptyLegsDetail
  /** The calculated cost; absent falls back to the route's stored estimate. */
  cost: PlanningRouteCost | undefined
  /** The shift cover cell, from formatShiftCoverCell. */
  shiftCover: string
}

/**
 * The shift cover column as one line: the same worst flag the table shows, then who and when.
 *
 * A covered route lists each booked person, since a double-manned route is covered by two
 * shifts. A route the check said nothing about carries the reason, blank when there is none.
 */
export function formatShiftCoverCell(
  cover: ShiftException[] | undefined,
  covered: CoveredRoute[] | undefined,
  notCheckedLabel: string | undefined,
): string {
  if (cover?.length) {
    const worst = worstException(cover)
    const { words } = describeCover(cover)
    const shifts = [...new Map(cover.filter((r) => r.shiftId).map((r) => [r.shiftId, r])).values()]
    const people = shifts.map((sh) =>
      `${displayPerson(sh.shiftPersonName) ?? "Unassigned"}${
        sh.shiftStatus && sh.shiftStatus !== "Filled" ? ` ${sh.shiftStatus}` : ""
      } ${formatWindow(sh.shiftStart, sh.shiftEnd) ?? ""}`.trim(),
    )
    return [COVER_LABEL[worst.exceptionType], ...people, ...words].join(" · ")
  }
  if (covered && covered.length > 0) {
    return [
      "Covered",
      ...covered.map((c) =>
        `${displayPerson(c.shiftPersonName) ?? "Unassigned"} ${
          formatWindow(c.shiftStart, c.shiftEnd) ?? ""
        }`.trim(),
      ),
    ].join(" · ")
  }
  return notCheckedLabel ?? ""
}

/** Shipper and haulage collections count as shippers; depots, deliveries and the rest do not. */
const SHIPPER_STOP_TYPES = new Set(["shipper_collection", "haulage_collection", "collection"])

/**
 * The whole table, in the order the routes are given.
 *
 * `getSite` is how the caller reaches the locations it has already loaded: a shipper is named by
 * its location's site name where there is one, else by the stop's own copy.
 */
export function buildPlanningListTable(
  items: PlanningListExportRoute[],
  context: {
    vehicleTypes: VehicleType[] | undefined
    config: Parameters<typeof applyPlannedMileageMultiplier>[1]
    getSite: (locationId: string) => { site_name?: string | null } | undefined
    /** What the login may be given. Left out, every column is written. */
    can?: { costs: boolean; shiftCover: boolean }
  },
): { headers: string[]; rows: string[][] } {
  const allRows = items.map(({ route, volume, capacityInfo, emptyLegs, cost, shiftCover }) => {
    const sortedStops = [...route.stops].sort((a, b) => a.sequence - b.sequence)

    const shipperNames = [
      ...new Set(
        sortedStops
          .filter((s) => {
            const stopType =
              (s as { slotType?: string }).slotType || (s as { stop_type?: string }).stop_type
            return SHIPPER_STOP_TYPES.has(stopType as string)
          })
          .map((s) => context.getSite(s.locationId)?.site_name || s.name || "")
          .filter(Boolean),
      ),
    ].join("; ")

    const vehicleTypeRef = route.vehicleType
    const vehicleType = findVehicleType(context.vehicleTypes, vehicleTypeRef)
    const vehiclePlanned = vehicleType?.display_name || vehicleType?.name || vehicleTypeRef || ""
    const vehicleRangeMiles =
      vehicleType?.range_miles != null ? vehicleType.range_miles.toString() : ""

    // Point to point (Haversine), with the planned mileage % applied.
    const rawDistanceMiles =
      sortedStops.length >= 2 ? calculateDistanceFromPlannedStops(sortedStops) : null
    const totalMileage =
      applyPlannedMileageMultiplier(rawDistanceMiles, context.config) ?? rawDistanceMiles

    const durationMinutes =
      route.plannedStartTime && route.plannedEndTime
        ? calculateDurationMinutes(route.plannedStartTime, route.plannedEndTime)
        : null

    const loadFactor =
      capacityInfo != null &&
      isFinite(capacityInfo.totalFillPercentage) &&
      !isNaN(capacityInfo.totalFillPercentage)
        ? capacityInfo.totalFillPercentage.toFixed(0)
        : ""

    const totalCost = cost ? cost.total_cost : route.estimatedCost || 0
    const revenue = cost?.total_haulage_delivery_revenue ?? 0
    const netCost = totalCost - revenue

    return [
      format(route.date, "yyyy-MM-dd"),
      format(route.date, "EEEE"),
      route.code,
      shipperNames,
      vehiclePlanned,
      vehicleRangeMiles,
      totalMileage != null ? totalMileage.toFixed(2) : "",
      format(route.plannedStartTime, "HH:mm"),
      format(route.plannedEndTime, "HH:mm"),
      durationMinutes != null ? Math.round(durationMinutes).toString() : "",
      route.totalStops.toString(),
      volume.toString(),
      loadFactor,
      shiftCover,
      emptyLegs.emptyPct != null ? `${emptyLegs.emptyPct}%` : emptyLegs.count > 0 ? "—" : "0%",
      emptyLegs.count.toString(),
      totalCost.toFixed(2),
      cost?.vehicle_lease_cost != null ? cost.vehicle_lease_cost.toFixed(2) : "",
      cost?.charging_cost != null ? cost.charging_cost.toFixed(2) : "",
      cost?.driver_wage_cost != null ? cost.driver_wage_cost.toFixed(2) : "",
      cost?.total_haulage_delivery_revenue != null
        ? cost.total_haulage_delivery_revenue.toFixed(2)
        : "",
      netCost.toFixed(2),
      cost?.cost_per_parcel != null ? cost.cost_per_parcel.toFixed(4) : "",
      cost?.charging_internal_miles != null ? cost.charging_internal_miles.toFixed(2) : "",
      cost?.charging_external_miles != null ? cost.charging_external_miles.toFixed(2) : "",
    ]
  })

  const { can } = context
  if (!can) return { headers: PLANNING_LIST_HEADERS, rows: allRows }

  const kept = PLANNING_LIST_HEADERS.map((header) => {
    const needs = COLUMN_NEEDS[header]
    return !needs || can[needs]
  })
  return {
    headers: PLANNING_LIST_HEADERS.filter((_, column) => kept[column]),
    rows: allRows.map((row) => row.filter((_, column) => kept[column])),
  }
}

/** The date part of an export's filename: the range, or today where there is no full range. */
export function exportDateRangeSlug(range: { from?: Date; to?: Date } | undefined): string {
  return range?.from && range?.to
    ? `${format(range.from, "yyyy-MM-dd")}_to_${format(range.to, "yyyy-MM-dd")}`
    : format(new Date(), "yyyy-MM-dd")
}
